import { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';

import { TilePuzzleGame } from '@/components/game/TilePuzzleGame';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { decodePuzzle, type PuzzleData } from '@/lib/puzzle';
import { usePuzzleByNaddr, useDeletePuzzle } from '@/hooks/usePuzzles';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';

function PlayContent({ puzzle }: { puzzle: PuzzleData }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-center max-w-lg">
        <h1 className="text-2xl font-bold text-foreground">{puzzle.title}</h1>
        {puzzle.description && (
          <p className="text-sm text-muted-foreground mt-2">{puzzle.description}</p>
        )}
      </div>
      <TilePuzzleGame puzzle={puzzle} />
    </div>
  );
}

export default function PlayPage() {
  const { naddr } = useParams<{ naddr: string }>();
  const [searchParams] = useSearchParams();
  const paramP = searchParams.get('p');
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutateAsync: deletePuzzle } = useDeletePuzzle();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useSeoMeta({
    title: 'Play — Somaria Tiles',
    description: 'Play a Hamilton Path tile puzzle inspired by Zelda: Oracle of Ages.',
  });

  // ── From URL param ────────────────────────────────────────────────────────
  const urlPuzzle = useMemo<PuzzleData | null>(() => {
    if (!paramP) return null;
    try {
      return decodePuzzle(paramP);
    } catch {
      return null;
    }
  }, [paramP]);

  // ── From Nostr naddr ───────────────────────────────────────────────────────
  const naddrQuery = usePuzzleByNaddr(naddr);

  const isOwn = naddrQuery.data && user?.pubkey === naddrQuery.data.pubkey;

  const handleDelete = async () => {
    if (!naddrQuery.data) return;
    setDeleting(true);
    try {
      await deletePuzzle({ pubkey: naddrQuery.data.pubkey, dTag: naddrQuery.data.dTag });
      toast({ title: 'Puzzle deleted', description: 'Your puzzle has been removed from Nostr.' });
      navigate('/browse');
    } catch (err) {
      toast({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (urlPuzzle) {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <PlayContent puzzle={urlPuzzle} />
        </div>
      </div>
    );
  }

  if (naddr) {
    if (naddrQuery.isLoading || (naddrQuery.isFetching && !naddrQuery.data)) {
      return (
        <div className="min-h-screen bg-background py-8 px-4 flex items-center justify-center">
          <div className="space-y-4 w-full max-w-md">
            <Skeleton className="h-8 w-48 mx-auto" />
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-12 w-48 mx-auto" />
          </div>
        </div>
      );
    }

    if (naddrQuery.data) {
      return (
        <div className="min-h-screen bg-background py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <PlayContent puzzle={naddrQuery.data.puzzle} />

            {/* Owner actions */}
            {isOwn && (
              <div className="flex justify-center gap-2 mt-6">
                <Button asChild variant="outline" size="sm">
                  <Link to={`/editor?naddr=${naddr}`}>Edit Puzzle</Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Puzzle
                </Button>
              </div>
            )}

            {/* Delete confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{naddrQuery.data.puzzle.title}"?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This publishes a NIP-09 deletion event to Nostr. Relays that honor deletion
                    requests will remove this puzzle. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      );
    }

    // Puzzle not found (after retries).
    if (naddrQuery.isError || !naddrQuery.data) {
      return (
        <div className="min-h-screen bg-background py-8 px-4 flex items-center justify-center">
          <Card className="border-dashed max-w-md">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground">
                Puzzle not found on Nostr relays. It may not have been published yet, or the relays may be unavailable.
              </p>
              <Button asChild className="mt-4">
                <Link to="/browse">Browse Puzzles</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // No puzzle specified — redirect to browse.
  return (
    <div className="min-h-screen bg-background py-8 px-4 flex items-center justify-center">
      <Card className="border-dashed max-w-md">
        <CardContent className="py-12 px-8 text-center">
          <p className="text-muted-foreground">
            No puzzle specified. Browse shared puzzles or create your own.
          </p>
          <div className="flex gap-2 justify-center mt-4">
            <Button asChild variant="outline">
              <Link to="/browse">Browse</Link>
            </Button>
            <Button asChild>
              <Link to="/editor">Create</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
